import { Home } from "@mui/icons-material";
import { Box, Breadcrumbs, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { COLORS } from "assets";
import { StatusPill } from "components";
import { BREAK_GLASS_APPROVED_EVENT, BREAK_GLASS_EVENT, MSG_CODE } from "config/constants";
import { incidentsApi, incidentsUseFixtures, visibleFacilities } from "incidents/api";
import { IncidentApiError } from "incidents/fixtureStore";
import { displayStatus, listDeadlineBadge, statusLabel, typeLabel } from "incidents/display";
import { Incident, IncidentStatus, IncidentType } from "incidents/types";
import PrivateLayout from "layouts/PrivateLayout";
import { useContextController } from "context";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { canAccessSite } from "utils";
import { fieldSx, primaryButtonSx, usePhrase } from "./common";

const STATUSES: Array<IncidentStatus | "all"> = [
  "all",
  "awaiting_staff_review",
  "in_documentation",
  "false_alert_pending_confirm",
  "submitted_locked",
  "closed"
];

const TYPES: Array<IncidentType | "all"> = [
  "all",
  "fall",
  "unsafe_interaction",
  "responsive_behaviour",
  "wandering_zone",
  "other"
];

function toneFor(status: IncidentStatus): "info" | "warning" | "error" | "success" | "neutral" {
  if (status === "pending_review" || status === "awaiting_staff_review") return "info";
  if (status === "false_alert_pending_confirm") return "warning";
  if (status === "in_documentation" || status === "open") return "warning";
  if (status === "submitted_locked") return "success";
  if (status === "closed") return "neutral";
  return "neutral";
}

export default function IncidentsList({
  preview = false,
  previewRole = "nurse"
}: {
  preview?: boolean;
  previewRole?: "nurse" | "supervisor";
}) {
  const phrase = usePhrase();
  const navigate = useNavigate();
  const [{ userData }] = useContextController();
  const [rows, setRows] = useState<Incident[]>([]);
  const [catalog, setCatalog] = useState<Incident[]>([]);
  const [status, setStatus] = useState<string>("all");
  const [type, setType] = useState<string>("all");
  const [facility, setFacility] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const now = new Date();

  useEffect(() => {
    const onApproved = () => setReloadKey((k) => k + 1);
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
  }, []);

  useEffect(() => {
    incidentsApi
      .list()
      .then(setCatalog)
      .catch(() => undefined);
  }, [reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    incidentsApi
      .list({
        status: status === "all" ? undefined : status,
        type: type === "all" ? undefined : type,
        facility: facility === "all" ? undefined : facility,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      })
      .then((incidents) => {
        if (cancelled) return;
        const visible =
          preview || incidentsUseFixtures() || userData?.type === "super"
            ? incidents
            : incidents.filter((incident) => canAccessSite(userData, incident.facility_id));
        setRows(visible);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        if (err instanceof IncidentApiError && err.msgCode === MSG_CODE.BREAK_GLASS_REQUIRED) {
          window.dispatchEvent(new CustomEvent(BREAK_GLASS_EVENT));
        }
        setError(err.message || phrase("Could not load incidents.", "Impossible de charger les incidents."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, type, facility, dateFrom, dateTo, phrase, preview, userData, reloadKey]);

  const homes = visibleFacilities(catalog.length ? catalog : rows);
  const base = preview ? "/dev/incidents" : "/incidents";

  const body = (
    <Box>
      <Breadcrumbs aria-label={phrase("Breadcrumb", "Fil d'Ariane")} sx={{ mb: 2 }}>
        <Tooltip title={phrase("Home", "Accueil")}>
          <IconButton component={Link} to="/" size="small" aria-label={phrase("Home", "Accueil")}>
            <Home fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography color="text.primary">{phrase("Incident documentation", "Documentation des incidents")}</Typography>
      </Breadcrumbs>
      <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700, color: COLORS.surface.primaryText, mb: 1 }}>
        {phrase("Incident documentation", "Documentation des incidents")}
      </Typography>
      <Typography sx={{ color: COLORS.surface.secondaryText, mb: 2, maxWidth: 760 }}>
        {phrase(
          "Every alert can become a record. NeuraVue fills in the system facts after a staff member confirms the alert. NeuraVue never sends the record to a regulator and never decides whether abuse occurred.",
          "Chaque alerte peut devenir un dossier. NeuraVue remplit les faits du système après la confirmation du personnel. NeuraVue n'envoie rien à un organisme et ne décide jamais s'il y a eu de l'abus."
        )}
      </Typography>
      {incidentsUseFixtures() ? (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: "#F8F1D8", color: "#3F3A1E" }} role="note">
          {phrase(
            "Example records are on screen so you can review the workflow before the incident service is connected. They are not a resident chart.",
            "Des exemples sont affichés pour revoir le parcours avant le branchement du service. Ce ne sont pas des dossiers d'usagers."
          )}
        </Box>
      ) : null}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2} role="group" aria-label={phrase("Status", "Statut")}>
        {STATUSES.map((value) => (
          <Button
            key={value}
            aria-pressed={status === value}
            onClick={() => setStatus(value)}
            sx={{
              ...primaryButtonSx,
              bgcolor: status === value ? COLORS.surface.primaryText : COLORS.surface.card,
              color: status === value ? COLORS.white : COLORS.surface.primaryText,
              border: `1px solid ${COLORS.surface.border}`
            }}
          >
            {value === "all" ? phrase("All", "Tous") : statusLabel(value, phrase("en", "fr") === "fr" ? "fr" : "en")}
          </Button>
        ))}
      </Box>
      <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
        <TextField
          select
          label={phrase("Type", "Type")}
          value={type}
          onChange={(event) => setType(event.target.value)}
          sx={{ ...fieldSx, minWidth: 240 }}
        >
          {TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {value === "all"
                ? phrase("All types", "Tous les types")
                : typeLabel(value, phrase("en", "fr") === "fr" ? "fr" : "en")}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={phrase("Home", "Milieu")}
          value={facility}
          onChange={(event) => setFacility(event.target.value)}
          sx={{ ...fieldSx, minWidth: 240 }}
        >
          <MenuItem value="all">{phrase("All homes", "Tous les milieux")}</MenuItem>
          {homes.map((home) => (
            <MenuItem key={home.id} value={home.id}>
              {home.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          label={phrase("From", "Du")}
          value={dateFrom}
          onChange={(event) => setDateFrom(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fieldSx, minWidth: 180 }}
        />
        <TextField
          type="date"
          label={phrase("To", "Au")}
          value={dateTo}
          onChange={(event) => setDateTo(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fieldSx, minWidth: 180 }}
        />
      </Box>
      <Box display="flex" gap={1} mb={2}>
        <Button component={Link} to={`${base}/confirmations`} sx={primaryButtonSx}>
          {phrase("Dismissal confirmations", "Confirmations de rejet")}
        </Button>
        <Button component={Link} to={`${base}/reports/dismissals`} sx={primaryButtonSx}>
          {phrase("Monthly dismissal report", "Rapport mensuel des rejets")}
        </Button>
      </Box>
      {error ? (
        <Typography role="alert" color="error">
          {error}
        </Typography>
      ) : null}
      {loading ? <Typography>{phrase("Loading records…", "Chargement des dossiers…")}</Typography> : null}
      {!loading && !rows.length ? (
        <Typography>{phrase("No records match these filters.", "Aucun dossier ne correspond.")}</Typography>
      ) : null}
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.5 }}>
        {rows.map((incident) => {
          const shown = displayStatus(incident);
          const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
          const badge = listDeadlineBadge(incident, now, locale);
          return (
            <Box
              component="li"
              key={incident.id}
              sx={{
                p: 2,
                border: `1px solid ${COLORS.surface.border}`,
                borderRadius: `${COLORS.radius.md}px`,
                bgcolor: COLORS.surface.card,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap"
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{incident.id}</Typography>
                <Typography sx={{ color: COLORS.surface.secondaryText }}>
                  {incident.facility_name} · {phrase("Room", "Chambre")} {incident.room || "—"} ·{" "}
                  {typeLabel(incident.type, locale)}
                </Typography>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
                  <StatusPill label={statusLabel(shown, locale)} tone={toneFor(shown)} />
                  {badge ? (
                    <StatusPill
                      label={badge.label}
                      tone={
                        badge.tone === "error"
                          ? "error"
                          : badge.tone === "warning"
                            ? "warning"
                            : badge.tone === "success"
                              ? "success"
                              : "info"
                      }
                    />
                  ) : null}
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate(`${base}/${incident.id}${preview ? `?as=${previewRole}` : ""}`)}
                sx={primaryButtonSx}
              >
                {phrase("Open record", "Ouvrir le dossier")}
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (preview) return <Box sx={{ p: 2, bgcolor: COLORS.surface.page, minHeight: "100vh" }}>{body}</Box>;
  return <PrivateLayout canAccess={() => true}>{body}</PrivateLayout>;
}
